import { type ResumeState, TEMPLATES, type TemplateId } from '@/types';
import { ResumePreview } from '@/components/ResumePreview';
import { useResumeStore } from '@/store/resume';
import { Sparkles, Loader2, Trash, Plus, Check, Crown, AlertCircle, FileDown, ArrowLeft, Eye } from 'lucide-react';
import { cn, getQuotaLabel, getAiUsageDescription, rewriteBullets, isAiApiConfigured } from '@/lib/utils';
import { useToast } from '@/components/Toast';
import html2pdf from 'html2pdf.js';
import { useEffect, useMemo, useState } from 'react';

const STEPS = [
  { label: 'Choose Template', idx: 1 },
  { label: 'Fill Details', idx: 2 },
  { label: 'Preview \u0026 Download', idx: 3 },
] as const;

interface ExperienceEditorProps {
  exp: ResumeState['experience'][0];
  onChange: (id: string, updates: Partial<ResumeState['experience'][0]>) => void;
  onRemove: (id: string) => void;
}

function ExperienceEditor({ exp, onChange, onRemove }: ExperienceEditorProps) {
  const [loadingBullet, setLoadingBullet] = useState<number | null>(null);
  const { aiUsage, incrementAiUsage, profile } = useResumeStore();
  const { showToast } = useToast();
  const aiConfigured = isAiApiConfigured();

  const remaining = Math.max(0, aiUsage.maxFreeCount - aiUsage.usedCount);

  const handleRewrite = async (idx: number) => {
    if (!aiConfigured) {
      showToast('AI rewrite is currently unavailable. It will be enabled once the AI service is configured.', 'warning');
      return;
    }
    if (aiUsage.usedCount >= aiUsage.maxFreeCount) {
      showToast('You have used your free AI rewrites. Upgrade to Pro for more.', 'warning');
      return;
    }
    const text = exp.bullets[idx];
    if (!text || text.trim().length < 3) {
      showToast('Please enter a bullet before using AI rewrite.', 'warning');
      return;
    }
    setLoadingBullet(idx);
    try {
      const result = await rewriteBullets(
        {
          resumeText: text,
          jobTitle: profile.fullName?.slice(0, 50) || '',
          anonymousId: aiUsage.anonymousId,
          model: 'gpt-4o-mini',
        },
        aiUsage.usedCount,
        aiUsage.maxFreeCount
      );
      const updated = [...exp.bullets];
      updated[idx] = result.bullets[0] || text;
      onChange(exp.id, { bullets: updated });
      incrementAiUsage();
      showToast('AI suggestion applied', 'success');
    } catch (err) {
      const error = err as { error?: string; message?: string };
      showToast(error.message || 'Something went wrong. Please try again.', 'error');
    } finally {
      setLoadingBullet(null);
    }
  };

  return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6 shadow-sm transition-shadow hover:shadow-md">
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-[#4A5568]">Company</label>
          <input
            type="text"
            value={exp.company}
            onChange={(e) => onChange(exp.id, { company: e.target.value })}
            className="mt-2 h-12 w-full rounded-lg border border-[#E2E8F0] bg-white px-4 text-base text-[#0A2540] transition-colors placeholder:text-[#718096] hover:border-[#CBD5E0] focus:border-[#00D4AA] focus:outline-none focus:ring-2 focus:ring-[#00D4AA]/25"
            placeholder="Acme Inc."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#4A5568]">Job Title</label>
          <input
            type="text"
            value={exp.title}
            onChange={(e) => onChange(exp.id, { title: e.target.value })}
            className="mt-2 h-12 w-full rounded-lg border border-[#E2E8F0] bg-white px-4 text-base text-[#0A2540] transition-colors placeholder:text-[#718096] hover:border-[#CBD5E0] focus:border-[#00D4AA] focus:outline-none focus:ring-2 focus:ring-[#00D4AA]/25"
            placeholder="Software Engineer"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#4A5568]">Start Date</label>
          <input
            type="month"
            value={exp.startDate}
            onChange={(e) => onChange(exp.id, { startDate: e.target.value })}
            className="mt-2 h-12 w-full rounded-lg border border-[#E2E8F0] bg-white px-4 text-base text-[#0A2540] transition-colors hover:border-[#CBD5E0] focus:border-[#00D4AA] focus:outline-none focus:ring-2 focus:ring-[#00D4AA]/25"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#4A5568]">End Date (or Present)</label>
          <input
            type="text"
            value={exp.endDate}
            onChange={(e) => onChange(exp.id, { endDate: e.target.value })}
            className="mt-2 h-12 w-full rounded-lg border border-[#E2E8F0] bg-white px-4 text-base text-[#0A2540] transition-colors placeholder:text-[#718096] hover:border-[#CBD5E0] focus:border-[#00D4AA] focus:outline-none focus:ring-2 focus:ring-[#00D4AA]/25"
            placeholder="2024-06 or present"
          />
        </div>
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-[#4A5568]">Bullet Points</label>
          <span className="text-xs font-medium text-[#00D4AA]">
            {getQuotaLabel(aiUsage.usedCount, aiUsage.maxFreeCount, aiUsage.subscription)} available
          </span>
        </div>
        <div className="mt-2 space-y-2">
          {exp.bullets.map((bullet, idx) => (
            <div key={idx} className="flex items-start gap-2">
              <textarea
                value={bullet}
                onChange={(e) => {
                  const updated = [...exp.bullets];
                  updated[idx] = e.target.value;
                  onChange(exp.id, { bullets: updated });
                }}
                className="min-h-[80px] flex-1 rounded-lg border border-[#E2E8F0] bg-white px-4 py-3 text-base text-[#0A2540] transition-colors placeholder:text-[#718096] hover:border-[#CBD5E0] focus:border-[#00D4AA] focus:outline-none focus:ring-2 focus:ring-[#00D4AA]/25"
                placeholder="Describe an achievement..."
                rows={2}
              />
              <button
                type="button"
                onClick={() => handleRewrite(idx)}
                disabled={loadingBullet === idx || remaining <= 0 || !aiConfigured}
                title={aiConfigured ? 'Improve this bullet with AI' : 'AI rewrite is currently unavailable'}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-[#E7F1FA] px-4 py-2.5 text-sm font-semibold text-[#0A2540] transition-colors hover:bg-[#D1E5F5] focus:outline-none focus:ring-2 focus:ring-[#00D4AA]/25 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loadingBullet === idx ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                AI Rewrite
                {!aiConfigured && <span className="ml-1 text-[10px] uppercase tracking-wider text-[#718096]">(soon)</span>}
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => onChange(exp.id, { bullets: [...exp.bullets, ''] })}
          className="mt-3 inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-[#0A2540] transition-colors hover:bg-[#F6F9FC]"
        >
          <Plus size={14} /> Add bullet
        </button>
      </div>

      <button
        type="button"
        onClick={() => onRemove(exp.id)}
        className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#E53E3E] transition-colors hover:text-[#C53030]"
      >
        <Trash size={14} /> Remove experience
      </button>
    </div>
  );
}

function TemplateCard({
  template,
  selected,
  isLocked,
  onSelect,
}: {
  template: (typeof TEMPLATES)[number];
  selected: boolean;
  isLocked: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={isLocked}
      className={cn(
        'group relative rounded-[24px] border bg-white p-5 text-left shadow-lg transition-all duration-300 ease-out',
        selected
          ? 'border-2 border-[#00D4AA] shadow-[0_0_30px_rgba(0,212,170,0.18)]'
          : 'border-[#E2E8F0] hover:-translate-y-2 hover:border-[#00D4AA]/40 hover:shadow-[0_0_30px_rgba(0,212,170,0.15)]',
        isLocked && 'cursor-not-allowed opacity-80'
      )}
    >
      {selected && (
        <span className="absolute -top-2 left-5 z-10 inline-flex items-center gap-1 rounded-md bg-[#00D4AA] px-2 py-1 text-xs font-bold text-[#0A2540] shadow-sm">
          <Check size={12} /> Selected
        </span>
      )}
      {isLocked && (
        <span className="absolute right-5 top-5 z-10 inline-flex items-center gap-1 rounded-md bg-[#635BFF] px-2 py-1 text-xs font-bold text-white">
          <Crown size={12} /> Pro
        </span>
      )}
      <div className={cn(
        'mb-4 aspect-[3/4] w-full overflow-hidden rounded-xl border border-[#E2E8F0] bg-[#F6F9FC] p-4 transition-transform duration-300 group-hover:scale-[1.03]',
        selected && 'border-[#00D4AA]/30'
      )}>
        <div className="h-2 w-1/3 rounded bg-[#0A2540]"></div>
        <div className="mt-3 space-y-2">
          <div className="h-1.5 w-full rounded bg-[#CBD5E0]"></div>
          <div className="h-1.5 w-5/6 rounded bg-[#CBD5E0]"></div>
          <div className="h-1.5 w-4/5 rounded bg-[#CBD5E0]"></div>
        </div>
        <div className="mt-4 h-1.5 w-2/3 rounded bg-[#CBD5E0]"></div>
        <div className="mt-2 space-y-1.5">
          <div className="h-1 w-full rounded bg-[#E2E8F0]"></div>
          <div className="h-1 w-11/12 rounded bg-[#E2E8F0]"></div>
          <div className="h-1 w-5/6 rounded bg-[#E2E8F0]"></div>
        </div>
      </div>
      <div className="font-semibold text-[#0A2540]">{template.name}</div>
      <div className="mt-1 text-sm text-[#718096]">{template.description}</div>
      {isLocked ? (
        <div className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-[#635BFF]/10 px-3 py-2 text-sm font-semibold text-[#635BFF]">
          <Crown size={14} /> Unlock with Pro
        </div>
      ) : selected ? (
        <div className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-[#00D4AA]/10 px-3 py-2 text-sm font-semibold text-[#00B894]">
          <Check size={14} /> Current template
        </div>
      ) : null}
    </button>
  );
}

export function Editor() {
  const state = useResumeStore();
  const {
    profile,
    experience,
    education,
    skills,
    selectedTemplate,
    aiUsage,
    setProfile,
    setExperience,
    addExperience,
    removeExperience,
    setEducation,
    addEducation,
    removeEducation,
    setSkills,
    setTemplate,
    clearAll,
  } = state;
  const { showToast } = useToast();
  const [skillInput, setSkillInput] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [step, setStep] = useState(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem('ai-free-resume-state') : null;
    if (!saved) return 1;
    try {
      const parsed = JSON.parse(saved) as ResumeState;
      const hasName = parsed.profile?.fullName?.trim().length > 0;
      const hasAnyData =
        hasName ||
        (parsed.experience?.some((e) => e.company?.trim() || e.title?.trim() || e.bullets?.some((b) => b?.trim())) ?? false) ||
        (parsed.education?.some((e) => e.school?.trim() || e.degree?.trim()) ?? false) ||
        (parsed.skills?.length ?? 0) > 0 ||
        (parsed.profile?.summary?.trim().length ?? 0) > 0;
      if (hasName || hasAnyData) return 3;
      return 2;
    } catch {
      return 1;
    }
  });

  const aiConfigured = isAiApiConfigured();

  useEffect(() => {
    setHydrated(true);
  }, []);

  const selectedTemplateInfo = useMemo(
    () => TEMPLATES.find((t) => t.id === selectedTemplate) || TEMPLATES[0],
    [selectedTemplate]
  );

  const handleAddSkill = () => {
    const trimmed = skillInput.trim();
    if (!trimmed) return;
    if (!skills.includes(trimmed)) {
      setSkills([...skills, trimmed]);
    }
    setSkillInput('');
  };

  const handleDownloadPdf = async () => {
    const el = document.getElementById('resume-preview');
    if (!el) {
      showToast('Could not generate PDF. Please try a different browser.', 'error');
      return;
    }
    setDownloading(true);
    try {
      const opt = {
        margin: 0,
        filename: `${profile.fullName || 'resume'}.pdf`,
        image: { type: 'jpeg' as const, quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4' as const, orientation: 'portrait' as const },
      };
      await html2pdf().set(opt).from(el).save();
      showToast('Your resume PDF is ready.', 'success');
    } catch {
      showToast('Could not generate PDF. Please try a different browser.', 'error');
    } finally {
      setDownloading(false);
    }
  };

  const canContinueToStep2 = selectedTemplate && !selectedTemplateInfo.isPro;
  const canGoToStep3 = canContinueToStep2 && profile.fullName.trim().length > 0;

  const handleStepClick = (nextStep: number) => {
    if (nextStep >= 2 && !canContinueToStep2) {
      showToast('Please choose a free template to continue.', 'warning');
      return;
    }
    if (nextStep >= 3 && !canGoToStep3) {
      showToast('Please fill in your full name before previewing.', 'warning');
      return;
    }
    setStep(nextStep);
  };

  if (!hydrated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0A2540]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 size={32} className="animate-spin text-[#00D4AA]" />
          <p className="text-sm text-white/75">Loading your resume editor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F9FC]">
      <header className="sticky top-0 z-30 border-b border-[#E2E8F0] bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex h-[72px] max-w-[1400px] items-center justify-between px-6">
          <a href="/" className="flex items-center gap-2">
            <span className="text-xl font-bold text-[#00D4AA]">ai-free-resume</span>
          </a>
          <button
            onClick={() => {
              clearAll();
              showToast('All local data cleared.', 'success');
            }}
            className="text-sm font-semibold text-[#E53E3E] transition-colors hover:text-[#C53030]"
          >
            Clear all data
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-4 py-6 md:px-6">
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0">
            {STEPS.map((s) => {
              const isActive = step === s.idx;
              const isCompleted = step > s.idx;
              return (
                <button
                  key={s.idx}
                  onClick={() => handleStepClick(s.idx)}
                  className={cn(
                    'flex shrink-0 items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition-all duration-200',
                    isActive
                      ? 'bg-[#00D4AA] text-[#0A2540] shadow-[0_0_24px_rgba(0,212,170,0.25)]'
                      : isCompleted
                        ? 'bg-[#0A2540] text-white hover:bg-[#112940]'
                        : 'border border-[#E2E8F0] bg-white text-[#4A5568] hover:border-[#CBD5E0]'
                  )}
                >
                  <span
                    className={cn(
                      'flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold',
                      isActive
                        ? 'bg-[#0A2540] text-[#00D4AA]'
                        : isCompleted
                          ? 'bg-white text-[#0A2540]'
                          : 'border border-[#CBD5E0] text-[#718096]'
                    )}
                  >
                    {isCompleted ? <Check size={12} /> : s.idx}
                  </span>
                  {s.label}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-2 rounded-lg border border-[#E2E8F0] bg-white px-3 py-2 text-sm text-[#4A5568]">
            <AlertCircle size={16} className="text-[#F6AD55]"></AlertCircle>
            <span>{getAiUsageDescription(aiUsage.usedCount, aiUsage.maxFreeCount, aiUsage.subscription)} {!aiConfigured && <span className="font-medium text-[#C05621]">— AI temporarily unavailable.</span>}</span>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[480px_1fr]">
          <div className="space-y-6">
            {step === 1 && (
              <div className="space-y-6">
                <div className="hero-glow rounded-2xl p-6 md:p-8">
                  <h2 className="text-2xl font-bold text-white">Choose a Template</h2>
                  <p className="mt-2 text-[15px] text-white/70">
                    Free users can choose Classic or Student. Pro templates are locked until you upgrade.
                  </p>
                  <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    {TEMPLATES.map((t) => (
                      <TemplateCard
                        key={t.id}
                        template={t}
                        selected={selectedTemplate === t.id}
                        isLocked={t.isPro}
                        onSelect={() => {
                          if (t.isPro) {
                            showToast('This template is available on Pro. Upgrade to unlock all templates.', 'warning');
                            return;
                          }
                          setTemplate(t.id as TemplateId);
                        }}
                      />
                    ))}
                  </div>
                  <button
                    onClick={() => handleStepClick(2)}
                    disabled={!canContinueToStep2}
                    className="btn-primary mt-8 inline-flex w-full items-center justify-center rounded-[10px] px-6 py-3.5 text-base font-bold disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Continue
                  </button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6">
                <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6 shadow-sm">
                  <h2 className="text-xl font-bold text-[#0A2540]">Personal Info</h2>
                  <div className="mt-4 grid gap-4">
                    {[
                      ['fullName', 'Full Name'],
                      ['email', 'Email'],
                      ['phone', 'Phone'],
                      ['location', 'Location'],
                    ].map(([key, label]) => (
                      <div key={key}>
                        <label className="block text-sm font-medium text-[#4A5568]">{label}</label>
                        <input
                          type="text"
                          value={profile[key as keyof typeof profile] as string}
                          onChange={(e) => setProfile({ [key]: e.target.value })}
                          className="mt-2 h-12 w-full rounded-lg border border-[#E2E8F0] bg-white px-4 text-base text-[#0A2540] transition-colors placeholder:text-[#718096] hover:border-[#CBD5E0] focus:border-[#00D4AA] focus:outline-none focus:ring-2 focus:ring-[#00D4AA]/25"
                        />
                      </div>
                    ))}
                    <div>
                      <label className="block text-sm font-medium text-[#4A5568]">Summary / Objective</label>
                      <textarea
                        value={profile.summary}
                        onChange={(e) => setProfile({ summary: e.target.value })}
                        className="mt-2 min-h-[100px] w-full rounded-lg border border-[#E2E8F0] bg-white px-4 py-3 text-base text-[#0A2540] transition-colors placeholder:text-[#718096] hover:border-[#CBD5E0] focus:border-[#00D4AA] focus:outline-none focus:ring-2 focus:ring-[#00D4AA]/25"
                        rows={3}
                      />
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6 shadow-sm">
                  <div className="mb-4 flex items-center justify-between">
                    <h2 className="text-xl font-bold text-[#0A2540]">Experience</h2>
                    <button
                      onClick={addExperience}
                      className="btn-primary inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold"
                    >
                      <Plus size={14} /> Add
                    </button>
                  </div>
                  {experience.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-[#E2E8F0] bg-[#F6F9FC] p-6 text-center">
                      <p className="text-sm text-[#718096]">No experience yet. Add your first role to get started.</p>
                      <button
                        onClick={addExperience}
                        className="btn-primary mt-3 inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold"
                      >
                        <Plus size={14} /> Add your first experience
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {experience.map((exp) => (
                        <ExperienceEditor
                          key={exp.id}
                          exp={exp}
                          onChange={setExperience}
                          onRemove={removeExperience}
                        />
                      ))}
                    </div>
                  )}
                </div>

                <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6 shadow-sm">
                  <div className="mb-4 flex items-center justify-between">
                    <h2 className="text-xl font-bold text-[#0A2540]">Education</h2>
                    <button
                      onClick={addEducation}
                      className="btn-primary inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold"
                    >
                      <Plus size={14} /> Add
                    </button>
                  </div>
                  <div className="space-y-4">
                    {education.map((edu) => (
                      <div key={edu.id} className="grid gap-4 rounded-xl border border-[#E2E8F0] bg-[#F6F9FC] p-5">
                        <div className="grid gap-4 md:grid-cols-2">
                          <div>
                            <label className="block text-sm font-medium text-[#4A5568]">School</label>
                            <input
                              type="text"
                              value={edu.school}
                              onChange={(e) => setEducation(edu.id, { school: e.target.value })}
                              className="mt-2 h-12 w-full rounded-lg border border-[#E2E8F0] bg-white px-4 text-base text-[#0A2540] transition-colors hover:border-[#CBD5E0] focus:border-[#00D4AA] focus:outline-none focus:ring-2 focus:ring-[#00D4AA]/25"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-[#4A5568]">Degree</label>
                            <input
                              type="text"
                              value={edu.degree}
                              onChange={(e) => setEducation(edu.id, { degree: e.target.value })}
                              className="mt-2 h-12 w-full rounded-lg border border-[#E2E8F0] bg-white px-4 text-base text-[#0A2540] transition-colors hover:border-[#CBD5E0] focus:border-[#00D4AA] focus:outline-none focus:ring-2 focus:ring-[#00D4AA]/25"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-[#4A5568]">Field of Study</label>
                            <input
                              type="text"
                              value={edu.field}
                              onChange={(e) => setEducation(edu.id, { field: e.target.value })}
                              className="mt-2 h-12 w-full rounded-lg border border-[#E2E8F0] bg-white px-4 text-base text-[#0A2540] transition-colors hover:border-[#CBD5E0] focus:border-[#00D4AA] focus:outline-none focus:ring-2 focus:ring-[#00D4AA]/25"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-[#4A5568]">Graduation Date</label>
                            <input
                              type="month"
                              value={edu.graduationDate}
                              onChange={(e) => setEducation(edu.id, { graduationDate: e.target.value })}
                              className="mt-2 h-12 w-full rounded-lg border border-[#E2E8F0] bg-white px-4 text-base text-[#0A2540] transition-colors hover:border-[#CBD5E0] focus:border-[#00D4AA] focus:outline-none focus:ring-2 focus:ring-[#00D4AA]/25"
                            />
                          </div>
                        </div>
                        <button
                          onClick={() => removeEducation(edu.id)}
                          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#E53E3E] transition-colors hover:text-[#C53030]"
                        >
                          <Trash size={14} /> Remove education
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6 shadow-sm">
                  <h2 className="text-xl font-bold text-[#0A2540]">Skills</h2>
                  <div className="mt-4 flex gap-2">
                    <input
                      type="text"
                      value={skillInput}
                      onChange={(e) => setSkillInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddSkill();
                        }
                      }}
                      className="h-12 flex-1 rounded-lg border border-[#E2E8F0] bg-white px-4 text-base text-[#0A2540] transition-colors placeholder:text-[#718096] hover:border-[#CBD5E0] focus:border-[#00D4AA] focus:outline-none focus:ring-2 focus:ring-[#00D4AA]/25"
                      placeholder="Type a skill and press Enter"
                    />
                    <button
                      onClick={handleAddSkill}
                      className="btn-primary inline-flex items-center gap-1.5 rounded-lg px-5 py-2 text-sm font-semibold"
                    >
                      Add
                    </button>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {skills.map((skill) => (
                      <span
                        key={skill}
                        className="inline-flex items-center gap-1 rounded-lg bg-[#E7F1FA] px-3 py-1.5 text-sm font-semibold text-[#0A2540]"
                      >
                        {skill}
                        <button
                          onClick={() => setSkills(skills.filter((s) => s !== skill))}
                          className="text-[#0A2540]/60 hover:text-[#0A2540]"
                          aria-label={`Remove ${skill}`}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                <button
                  onClick={() => handleStepClick(3)}
                  disabled={!canGoToStep3}
                  className="btn-primary inline-flex w-full items-center justify-center rounded-[10px] px-6 py-3.5 text-base font-bold disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Preview Resume
                </button>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6 shadow-sm">
                  <h2 className="text-xl font-bold text-[#0A2540]">Ready to Download</h2>
                  <p className="mt-1 text-sm text-[#718096]">
                    Template: <span className="font-semibold text-[#0A2540]">{selectedTemplateInfo.name}</span>
                  </p>

                  <div className={cn(
                    'mt-4 rounded-lg border p-4 text-sm',
                    aiUsage.usedCount >= aiUsage.maxFreeCount
                      ? 'border-[#F6AD55]/40 bg-[#FFFAF0] text-[#C05621]'
                      : 'border-[#00D4AA]/30 bg-[#00D4AA]/8 text-[#0A2540]'
                  )}
                  >
                    <div className="flex items-start gap-2">
                      <AlertCircle size={16} className={cn('mt-0.5 shrink-0', aiUsage.usedCount >= aiUsage.maxFreeCount ? 'text-[#F6AD55]' : 'text-[#00D4AA]')} />
                      <div>
                        <strong>Free AI rewrites:</strong> {getQuotaLabel(aiUsage.usedCount, aiUsage.maxFreeCount, aiUsage.subscription)} available. Upgrade to Pro for 50 per day.
                        {!aiConfigured && <p className="mt-1 text-xs font-medium text-[#C05621]">AI rewrite is temporarily unavailable and will be enabled once the AI service is configured.</p>}
                        <p className="mt-1 text-xs text-[#718096]">AI-generated suggestions may need review.</p>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={handleDownloadPdf}
                    disabled={downloading}
                    className="btn-primary mt-5 inline-flex w-full items-center justify-center gap-2 rounded-[10px] px-6 py-3.5 text-base font-bold disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {downloading ? <Loader2 size={18} className="animate-spin" /> : <FileDown size={18} />}
                    {downloading ? 'Preparing PDF...' : 'Download PDF'}
                  </button>

                  <button
                    onClick={() => handleStepClick(2)}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-[10px] border border-[#0A2540] bg-white px-6 py-3.5 text-base font-bold text-[#0A2540] transition-colors hover:bg-[#F6F9FC]"
                  >
                    <ArrowLeft size={18} /> Edit Details
                  </button>

                  <button
                    onClick={() => setPreviewOpen(!previewOpen)}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-[10px] border border-[#E2E8F0] bg-white px-6 py-3.5 text-base font-bold text-[#4A5568] transition-colors hover:bg-[#F6F9FC] lg:hidden"
                  >
                    <Eye size={18} /> {previewOpen ? 'Hide Preview' : 'Show Preview'}
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className={cn('lg:block', previewOpen || step === 3 ? 'block' : 'hidden')}>
            <div className="sticky top-[88px] overflow-x-auto rounded-2xl bg-white p-4 shadow-lg">
              <div id="resume-preview" className="mx-auto w-full max-w-[210mm]">
                <ResumePreview state={state} />
              </div>
            </div>
          </div>
        </div>

        {/* Mobile bottom fixed action bar */}
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-[#E2E8F0] bg-white p-4 lg:hidden">
          <div className="mx-auto flex max-w-[1400px] items-center gap-3">
            <button
              onClick={() => setPreviewOpen(!previewOpen)}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-[10px] border border-[#E2E8F0] bg-white px-4 py-3 text-sm font-semibold text-[#4A5568]"
            >
              <Eye size={16} /> {previewOpen ? 'Hide Preview' : 'Preview'}
            </button>
            <button
              onClick={step === 3 ? handleDownloadPdf : () => handleStepClick(3)}
              disabled={step !== 3 ? !canGoToStep3 : downloading}
              className="btn-primary inline-flex flex-[2] items-center justify-center gap-2 rounded-[10px] px-4 py-3 text-sm font-semibold disabled:opacity-50"
            >
              <FileDown size={16} />
              {step === 3 ? 'Download PDF' : 'Continue to Download'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Editor;
